import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardActions,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

const MAX_ATTACHMENT_SIZE_MB = 1.99;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

const HelpEmailForm = ({ isOpen, onClose, onSend, initialAttachments, handleDefScreenshotDelete }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState(initialAttachments);
  const [error, setError] = useState("");

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    const totalSize = [...attachments, ...files.map(file => ({ file }))]
      .reduce((acc, curr) => acc + curr.file.size, 0);

    if (totalSize > MAX_ATTACHMENT_SIZE_BYTES) {
      setError(`Total attachment size must not exceed ${MAX_ATTACHMENT_SIZE_MB} MB.`);
      return;
    }

    const previews = files.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setAttachments((prev) => [...prev, ...previews]);
  };

  useEffect(() => {
    if (isOpen && initialAttachments[0]?.def) {
      setAttachments(initialAttachments);
    }
  }, [isOpen, initialAttachments]);
  

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      if (updated[index].def) {
        handleDefScreenshotDelete();
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1]; // Strip the data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const handleSend = async () => {
    try {
      const attachmentMap = {};
      for (const att of attachments) {
        const base64 = await fileToBase64(att.file);
        attachmentMap[att.file.name] = base64;
      }
      if (!subject || !body) {
        setError("Please provide subject and some details on the issue before sending the email.")
        return;
      }
      onSend({ subject, body, attachments: attachmentMap });
      setSubject("");
      setBody("");
      setAttachments([]);
      setError("");
      onClose();
    } catch (err) {
      setError("Failed to process attachments. Please try again.");
    }
  };

  const handleCloseError = () => {
    setError("");
  };

  const handleClose = () => {
    setSubject("");
    setBody("");
    setAttachments([]);
    setError("");
    onClose();
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="h6">Contact Support</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Please describe your issue briefly"
          multiline
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          margin="normal"
        />
        <Button variant="outlined" component="label" sx={{ mt: 2 }}>
          Attach Screenshots
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleAttachmentChange}
          />
        </Button>

        {attachments.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>
              Attached Screenshots:
            </Typography>
            <Grid container spacing={2}>
              {attachments.map((att, idx) => (
                <Grid size={4} key={idx}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="100"
                      image={att.preview}
                      alt={`screenshot-${idx}`}
                    />
                    <CardActions>
                      <IconButton onClick={() => removeAttachment(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSend} variant="contained" color="primary">
          Send
        </Button>
      </DialogActions>
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default HelpEmailForm;
